/**
 * CRM Multi-Compagnia · Checklist Export SIA Omnia 8 (8 passaggi)
 * Stato dei checkbox in localStorage.
 */
import { useState, useEffect } from "react";
import { CheckCircle2, Copy, ChevronDown, ChevronRight, ArrowLeft } from "lucide-react";
import { Btn, toast } from "@/components/crm-multi/shared";

const EMAIL_SIA = `Spett.le SIA S.p.A.,

con la presente richiediamo l'esportazione completa di tutti i dati del nostro gestionale
Omnia 8 nei seguenti tracciati standard:

- Anagrafica clienti (PF e Aziende)
- Compagnie e Prodotti
- Polizze in corso, scadute e disdette
- Sinistri (aperti e chiusi)
- Incassi e insoluti
- Subagenti / collaboratori
- Documentazione collegata (riferimenti file)

Formati richiesti: CSV con separatore punto-e-virgola, encoding UTF-8 o Excel (.xlsx).
Periodo: l'intero storico disponibile.

Vi preghiamo di confermare disponibilità e tempistiche di consegna.

Cordiali saluti,
[Nome Agenzia]`;

const STEPS = [
  { id: 1, title: "Richiesta a SIA", desc: "Invia l'email standard a SIA per richiedere l'export completo dei dati storici." },
  { id: 2, title: "Export manuale da Omnia", desc: "In alternativa, esegui l'export manuale dal menu 'Strumenti > Export' di Omnia, selezionando uno alla volta i tracciati: clienti, compagnie, polizze, sinistri, incassi." },
  { id: 3, title: "Caricamento file nel CRM", desc: "Vai alla Migrazione SIA → Modalità A e carica un file alla volta, partendo da: 1) compagnie, 2) clienti, 3) polizze, 4) sinistri, 5) incassi." },
  { id: 4, title: "Mapping colonne", desc: "Durante l'upload il sistema suggerisce automaticamente la corrispondenza tra colonne del file e campi CRM. Verifica e correggi se necessario. Salva come default per i prossimi import." },
  { id: 5, title: "Simulazione (dry-run)", desc: "Prima dell'importazione definitiva esegui la simulazione: vedrai quanti record verranno creati, aggiornati o saltati, con eventuali errori segnalati riga per riga." },
  { id: 6, title: "Correzione errori", desc: "Scarica il CSV degli errori, correggi nel file sorgente, ricarica e ri-simula finché non risultano errori bloccanti." },
  { id: 7, title: "Importazione definitiva", desc: "Una volta che la simulazione è verde, esegui l'import definitivo. Operazione idempotente: i record già presenti vengono AGGIORNATI, non duplicati." },
  { id: 8, title: "Verifica quadratura", desc: "Nella pagina Quadratura inserisci i totali dichiarati da SIA (clienti, polizze, premi). Il sistema calcola le differenze e produce un report scaricabile per documentare la migrazione." },
];

const KEY = "sia_checklist_state_v1";

export default function SiaChecklistPage({ onBack }) {
  const [done, setDone] = useState({});
  const [expanded, setExpanded] = useState(1);

  useEffect(() => {
    try { setDone(JSON.parse(localStorage.getItem(KEY) || "{}")); } catch {}
  }, []);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(done)); }, [done]);

  const completati = Object.values(done).filter(Boolean).length;
  const copyEmail = () => {
    navigator.clipboard.writeText(EMAIL_SIA);
    toast("Email copiata negli appunti", "success");
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="sia-checklist-page">
      <header className="flex items-center gap-2">
        {onBack && <Btn variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4" /></Btn>}
        <div>
          <h2 className="text-xl font-semibold">📋 Checklist Export SIA Omnia 8</h2>
          <p className="text-sm text-zinc-500">{completati}/8 passaggi completati</p>
        </div>
      </header>

      <div className="bg-emerald-600 h-1.5 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-400 transition-all" style={{ width: `${(completati / 8) * 100}%` }} />
      </div>

      <ol className="space-y-2">
        {STEPS.map(s => {
          const isOpen = expanded === s.id;
          const isDone = !!done[s.id];
          return (
            <li key={s.id} className={`bg-white border rounded-lg ${isDone ? "border-emerald-300" : "border-zinc-200"}`} data-testid={`sia-step-${s.id}`}>
              <button onClick={() => setExpanded(isOpen ? 0 : s.id)} className="w-full text-left flex items-center gap-3 p-3">
                <input type="checkbox" checked={isDone} onChange={(e) => { e.stopPropagation(); setDone({ ...done, [s.id]: e.target.checked }); }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-emerald-600 rounded" data-testid={`sia-step-${s.id}-check`} />
                <span className={`text-sm font-medium flex-1 ${isDone ? "line-through text-zinc-400" : "text-zinc-900"}`}>
                  Step {s.id}. {s.title}
                </span>
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
              </button>
              {isOpen && (
                <div className="px-4 pb-4 ml-7 text-sm text-zinc-600">
                  <p>{s.desc}</p>
                  {s.id === 1 && (
                    <div className="mt-3 bg-zinc-50 border border-zinc-200 rounded p-3">
                      <pre className="text-xs whitespace-pre-wrap font-sans text-zinc-700">{EMAIL_SIA}</pre>
                      <div className="mt-2 flex justify-end">
                        <Btn size="sm" variant="primary" onClick={copyEmail} data-testid="sia-step-1-copy">
                          <Copy className="w-3.5 h-3.5" /> Copia testo
                        </Btn>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {completati === 8 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" /> Tutti i passaggi completati! La migrazione SIA è stata documentata correttamente.
        </div>
      )}
    </div>
  );
}
